import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import { useNavigate } from "react-router-dom";
import TablePaginationFooter from "components/common/TablePaginationFooter";

export default function TemplatesTable({
  templates,
  deleteTemplate,
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalElements,
}) {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = (id) => {
    setTemplateToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTemplate(templateToDelete);
    } catch (error) {
      console.log(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      accessorKey: "code",
      header: "Code",
      cell: ({ getValue }) => {
        const text = getValue() || "None";
        return (
          <div className="py-1">
            <h6 className="mb-0 text-900 fs-10">{text}</h6>
          </div>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "subject",
      header: "Subject",
      cell: ({ getValue }) => {
        const text = getValue() || "No subject provided";
        return (
          <span
            className="text-500 text-truncate d-inline-block"
            style={{
              maxWidth: 180,
              fontSize: "0.75rem",
              fontWeight: 500,
              lineHeight: 1.4,
            }}
            title={getValue()}
          >
            {text}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "clientApp",
      header: "Client App",
      cell: ({ getValue }) => {
        const text = getValue();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{
              color: "#343a40",
              maxWidth: 120,
              fontSize: "0.75rem",
              fontWeight: 500,
              lineHeight: 1.4,
            }}
            title={getValue()}
          >
            {text}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "locale",
      cell: ({ getValue }) => (
        <span
          className="text-500 d-inline-block"
          style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}
        >
          {getValue() || "N/A"}
        </span>
      ),
      header: "Locale",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const style = {
          backgroundColor: value ? "#e8f7e3" : "#fbe9e7",
          color: value ? "#2e7d32" : "#c62828",
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          border: value ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
          cursor: "default",
          fontWeight: 600,
        };
        const content = value == true ? "Active" : "Disabled";
        return <span style={style}>{content}</span>;
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Guard privilege={PRIV.VIEW_FEEDBACKS}>
                <Dropdown.Item
                  onClick={() =>
                    navigate(`/account/message-templates/${item.id}`)
                  }
                >
                  View
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.VIEW_FEEDBACKS}>
                <Dropdown.Item
                  onClick={() =>
                    navigate(`/account/message-templates/update/${item.id}`)
                  }
                >
                  Edit
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.VIEW_FEEDBACKS}>
                <Dropdown.Item
                  onClick={() => confirmDelete(item.id)}
                  className="text-danger"
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: templates,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap fs-12"
          tableProps={{
            bordered: true,
            striped: true,
            className: "mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            onPrev={
              page > 0 && totalElements > 0
                ? () => setPage((prev) => Math.max(prev - 1, 0))
                : undefined
            }
            onNext={
              page < totalPages - 1 && totalElements > 0
                ? () => setPage((prev) => Math.min(prev + 1, totalPages - 1))
                : undefined
            }
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="template"
        loading={isDeleting}
      />
    </>
  );
}
